import * as THREE from "three";
import { LayerItem } from "../data-structures/layer-item";
import { LayerItemType } from "../data-structures/layer-item-type";
import { LayerStatus } from "../data-structures/layer-status";
import { ViewType } from "../data-structures/view-type";
import { Profile } from "./profile";
import { generateViewerColorPalette, getViewerColorFromId } from "./viewer-color-utils";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { Line2 } from "three/examples/jsm/lines/Line2";

declare const Potree: any;
declare const proj4: any;

type ProfileGroup = { label: string; profiles: Profile[] };

/**
 * Manager responsible for managing profile-related functionality including
 * profile visualization, selection, and hover interactions.
 */
export class ProfileManager {
	// Constants for hover detection
	private static readonly HOVER_TOLERANCE_2D = 0.02;
	private static readonly HOVER_TOLERANCE_3D = 0.015;
	private static readonly HOVER_MARKER_SIZE = 0.3;
	private static readonly HOVER_MARKER_OUTLINE_SIZE = 0.35;

	private readonly profileHoverMarkers: Map<string, THREE.Object3D> = new Map();
	private current3DHoveredProfileId: string | null = null;
	public profiles: any[] = [];
	public selectedProfiles: Profile[] = [];
	public profileLayers: LayerItem[] = [];
	public activeProfileLayer: LayerItem | null = null;

	public constructor(private readonly viewer: any) {}

	public get hoveredProfileId(): string | null {
		return this.current3DHoveredProfileId;
	}

	public addLayer(layerName: string): void {
		if (this.profileLayers.find((x: LayerItem) => x.name == layerName) != null) {
			return;
		}
		const layer: LayerItem = {
			id: layerName,
			name: layerName,
			layerType: LayerItemType.Profiles,
			visible: true,
			layerStatus: LayerStatus.ProcessedAndLoaded
		};
		this.profileLayers.push(layer);
		this.viewer.updateLayers();
	}

	public deleteLayer(layerName: string): void {
		const layer = this.profileLayers.find((x: LayerItem) => x.name == layerName);
		if (!layer) {
			return;
		}
		let i = 0;
		while (i < this.profiles.length) {
			const profile = this.profiles[i];

			if (profile.userData.layerName == layer.name) {
				this.viewer.profilesGroup.remove(profile);
				this.profiles.splice(i, 1);
			} else {
				i++;
			}
		}
		this.profileLayers.splice(this.profileLayers.indexOf(layer), 1);

		if (this.activeProfileLayer?.id == layer.id) {
			this.activeProfileLayer = null;
		}

		this.viewer.updateLayers();
	}

	public async load(url: string): Promise<boolean> {
		try {
			return await this.loadProfilesFromJsonUrl(url);
		} catch (e) {
			console.error(`Error loading profile data: ${e}`);
			return false;
		}
	}

	public updateUnits(unit: string): void {
		for (const profile of this.profiles) {
			profile.unit = unit;
		}
	}

	public handleMultiSelection(profile: Profile): void {
		const index = this.selectedProfiles.findIndex((p: Profile) => p.uuid === profile.uuid);

		if (index >= 0) {
			this.viewer.selectObject(this.selectedProfiles[index], false);
			this.selectedProfiles.splice(index, 1);
		} else {
			if (this.selectedProfiles.length > 0) {
				const currentGroupKey = (profile as any).userData?.groupKey;
				const existingGroupKey = (this.selectedProfiles[0] as any).userData?.groupKey;

				if (currentGroupKey !== existingGroupKey) {
					console.log(`Cannot mix profile types: "${existingGroupKey}" and "${currentGroupKey}"`);
					return;
				}
			}

			this.selectedProfiles.push(profile);
			this.viewer.selectObject(profile, true);
		}

		if (this.selectedProfiles.length > 0) {
			this.viewer.selectedElement = {
				isMultiProfileSelection: true,
				profiles: this.selectedProfiles,
				elementType: "multiProfile"
			};
		} else {
			this.viewer.selectedElement = null;
		}
	}

	public clearMultiSelection(): void {
		for (const profile of this.selectedProfiles) {
			this.viewer.selectObject(profile, false);
		}
		this.selectedProfiles = [];
	}

	public deselectAll(): void {
		this.viewer.deselectElement();
		this.clearMultiSelection();
		this.viewer.onSelectedElementChanged.emit(null);
	}

	public updateLine2Resolution(): void {
		const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		const width = renderAreaSize.width;
		const height = renderAreaSize.height;

		for (const profile of this.profiles) {
			if (profile.updateLine2Resolution) {
				profile.updateLine2Resolution(width, height);
			}
		}
	}

	public updateVisibility(profileId: string, visible: boolean): void {
		const profile = this.profiles.find((p: Profile) => p.uuid === profileId);
		if (profile) {
			profile.updateVisibility(visible);
		}
	}

	public updateLayerObjectVisibility(): void {
		const visibleProfileIds = new Set<string>();

		this.profileLayers.forEach((layer: LayerItem) => {
			if (layer.children) {
				layer.children.forEach((child) => {
					if (child.visible) {
						visibleProfileIds.add(child.id);
					}
				});
			} else if (layer.visible) {
				visibleProfileIds.add(layer.id);
			}
		});

		for (const profile of this.profiles) {
			const visible = visibleProfileIds.has(profile.uuid);
			if (profile.visible !== visible) {
				profile.updateVisibility(visible);
			}
			const marker = this.profileHoverMarkers.get(profile.uuid);
			if (marker) {
				marker.visible = !!(visible && marker.userData?.hasPosition);
			}
		}
	}

	public setHoverMarker(profileId: string, position?: THREE.Vector3 | { x: number; y: number; z: number } | null): void {
		const profile = this.profiles.find((p: Profile) => p.uuid === profileId);
		if (!profile) return;

		let marker = this.profileHoverMarkers.get(profileId);
		if (!position) {
			if (marker) {
				marker.userData.hasPosition = false;
				marker.visible = false;
			}
			return;
		}

		const pos = position instanceof THREE.Vector3
			? position
			: new THREE.Vector3(position.x, position.y, position.z);

		if (!marker) {
			const color = (profile.material?.color as THREE.Color) ?? new THREE.Color(0xffffff);

			const outlineGeom = new THREE.SphereGeometry(ProfileManager.HOVER_MARKER_OUTLINE_SIZE, 16, 16);
			const outlineMat = new THREE.MeshBasicMaterial({
				color: 0xffffff,
				transparent: true,
				opacity: 1.0,
				depthTest: false,
				depthWrite: false
			});
			const outlineMesh = new THREE.Mesh(outlineGeom, outlineMat);
			outlineMesh.renderOrder = 998;

			const geom = new THREE.SphereGeometry(ProfileManager.HOVER_MARKER_SIZE, 16, 16);
			const mat = new THREE.MeshBasicMaterial({
				color,
				transparent: true,
				opacity: 0.95,
				depthTest: false,
				depthWrite: false
			});
			const innerMesh = new THREE.Mesh(geom, mat);
			innerMesh.renderOrder = 999;

			marker = new THREE.Group();
			marker.add(outlineMesh);
			marker.add(innerMesh);
			(marker as any).innerMesh = innerMesh;
			(marker as any).outlineMesh = outlineMesh;

			this.viewer.profilesGroup.add(marker);
			this.profileHoverMarkers.set(profileId, marker);
		} else {
			const c = (profile.material?.color as THREE.Color);
			const innerMesh = (marker as any).innerMesh;
			if (c && innerMesh) {
				(innerMesh.material as THREE.MeshBasicMaterial).color.copy(c);
			}
		}

		marker.position.copy(pos);
		marker.userData.hasPosition = true;
		marker.visible = !!profile.visible;
		this.scaleHoverMarker(marker, pos);
	}

	public clearHoverMarkers(profileIds?: string[]): void {
		if (profileIds && profileIds.length) {
			for (const id of profileIds) {
				const marker = this.profileHoverMarkers.get(id);
				if (marker && marker.parent) marker.parent.remove(marker);
				this.profileHoverMarkers.delete(id);
			}
			return;
		}
		for (const [, marker] of this.profileHoverMarkers) {
			if (marker.parent) marker.parent.remove(marker);
		}
		this.profileHoverMarkers.clear();
	}

	public refreshHoverMarkersAfterViewChange(): void {
		for (const [, marker] of this.profileHoverMarkers) {
			if (!marker.userData?.hasPosition) continue;

			const innerMesh = (marker as any).innerMesh;
			const outlineMesh = (marker as any).outlineMesh;
			if (innerMesh && innerMesh.material) {
				(innerMesh.material as THREE.MeshBasicMaterial).needsUpdate = true;
				innerMesh.renderOrder = 999;
			}
			if (outlineMesh && outlineMesh.material) {
				(outlineMesh.material as THREE.MeshBasicMaterial).needsUpdate = true;
				outlineMesh.renderOrder = 998;
			}
		}
	}

	public updateHoverMarkerScales(camera: any, clientWidth: number, clientHeight: number): void {
		for (const [, marker] of this.profileHoverMarkers) {
			if (!marker.userData?.hasPosition || !marker.visible) continue;
			const distance = camera.position.distanceTo(marker.position);
			const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
			const scale = (15 / pr);
			marker.scale.set(scale, scale, scale);
		}
	}

	public handleHoverDetection(): void {
		const hoverRaycaster = new THREE.Raycaster();
		hoverRaycaster.setFromCamera(this.viewer.mousePosition, this.viewer.scene.getActiveCamera());
		hoverRaycaster.params.Line = { threshold: 0.5 };

		const visibleProfiles = this.profiles.filter((profile: Profile) => profile.visible);
		const profileIntersections = hoverRaycaster.intersectObjects(visibleProfiles, true);

		let bestIntersection: THREE.Intersection | null = null;
		let bestScreenDist = Infinity;
		let bestRayDist = Infinity;
		const tol = this.viewer.viewType === ViewType.View2D
			? ProfileManager.HOVER_TOLERANCE_2D
			: ProfileManager.HOVER_TOLERANCE_3D;
		const eps = 1e-6;

		for (const intersection of profileIntersections) {
			const camera = this.viewer.scene.getActiveCamera();
			const intersectionScreenPos = intersection.point.clone().project(camera);
			const mouseScreenDistance = Math.hypot(
				intersectionScreenPos.x - this.viewer.mousePosition.x,
				intersectionScreenPos.y - this.viewer.mousePosition.y
			);

			if (mouseScreenDistance >= tol) continue;

			if (this.viewer.viewType === ViewType.View2D) {
				if (
					!bestIntersection ||
					intersection.distance + eps < bestRayDist ||
					(Math.abs(intersection.distance - bestRayDist) <= eps && this.isDrawOrderOnTop(intersection, bestIntersection))
				) {
					bestIntersection = intersection;
					bestRayDist = intersection.distance;
					bestScreenDist = mouseScreenDistance;
				}
			} else if (!bestIntersection || mouseScreenDistance < bestScreenDist) {
				bestIntersection = intersection;
				bestScreenDist = mouseScreenDistance;
				bestRayDist = intersection.distance;
			}
		}

		if (bestIntersection) {
			this.clearAll3DHoverMarkers();
			this.handleProfileHover(bestIntersection);
		} else {
			this.clearAll3DHoverMarkers();
		}
	}

	public getHoveredProfile(): Profile | null {
		if (!this.current3DHoveredProfileId) return null;
		return this.profiles.find((p: Profile) => p.uuid === this.current3DHoveredProfileId) ?? null;
	}

	public zoomToProfile(profile: any): void {
		if (!profile) return;
		const box = new THREE.Box3().setFromObject(profile);
		this.viewer.fitToScreen(box);
	}

	private async loadProfilesFromJsonUrl(url: string): Promise<boolean> {
		// Profile loading
		const loader = new THREE.FileLoader(THREE.DefaultLoadingManager);
		const raw = await loader.loadAsync(url);
		let dataset: any;
		try { dataset = JSON.parse(typeof raw === "string" ? raw : (raw as any)); } catch (e) { console.error("Invalid JSON profiles dataset", e); return false; }

		const toNum = (v: any) => typeof v === "number" ? v : parseFloat(v);
		const toVecs3 = (pts: any[]) => (pts || []).map(p => new THREE.Vector3(toNum(p[0]), toNum(p[1]), toNum(p[2])));
		const toChartPoints = (pts2: any[] | undefined, pts3: THREE.Vector3[]) => {
			if (!Array.isArray(pts2) || pts2.length === 0) return [] as Array<{ distance: number; elevation: number }>;
			const first = pts2[0];
			const firstX = toNum(first[0]);
			let elevationOffset = 0;
			if (pts3.length > 0) {
				const baseY = toNum(first[1]) || 0;
				elevationOffset = pts3[0].z - baseY;
			}
			return pts2.map((row) => {
				const rawDistance = toNum(row[0]);
				const distance = Number.isFinite(rawDistance) ? rawDistance : (Number.isFinite(firstX) ? firstX : 0);
				const elevation = toNum(row[1]) + elevationOffset;
				return { distance, elevation };
			});
		};

		if (this.profiles.length > 0) {
			for (const existing of this.profiles) {
				if (existing.parent) existing.parent.remove(existing);
			}
		}
		this.profiles = [];
		this.profileLayers = [];
		this.selectedProfiles = [];
		this.clearHoverMarkers();
		if (this.viewer.profilesGroup) {
			this.viewer.profilesGroup.clear();
		}

		const profileGroups = new Map<string, ProfileGroup>();
		const ensureGroup = (groupId: string, label: string) => {
			if (!profileGroups.has(groupId)) {
				profileGroups.set(groupId, { label, profiles: [] });
			}
			return profileGroups.get(groupId)!;
		};
		let profileCounter = 0;

		if (Array.isArray((dataset as any)?.profiles) && Array.isArray((dataset as any)?.groups)) {
			const records: any[] = (dataset as any).profiles;
			const groupMapping = new Map<string, { key: string; label: string; groupIndex: number; baseGroupIndex: number; distanceValue: number | null; isCross: boolean }>();
			const groupInfoByKey = new Map<string, { label: string; groupIndex: number; baseGroupIndex: number; distanceValue: number | null; isCross: boolean }>();
			const parentGroupsByDefinition = new Map<string, { groupNumber: number; groupKey: string }>();
			let nextGroupNumber = 0;

			const toDefinitionKey = (rec: any): string => {
				if (!Array.isArray(rec?.definition_line)) {
					return `id_${String(rec?.id ?? "")}`;
				}
				const sanitized = rec.definition_line.map((pair: any[]) => {
					if (!Array.isArray(pair)) return [];
					return pair.map((value: any) => toNum(value));
				});
				return JSON.stringify(sanitized);
			};

			const isParentRecord = (rec: any): boolean => {
				if (!rec) return false;
				if (rec.parent_profile === undefined || rec.parent_profile === null) return true;
				const parentValue = String(rec.parent_profile).trim();
				return parentValue.length === 0;
			};

			const getOrCreateParentGroup = (rec: any) => {
				const definitionKey = toDefinitionKey(rec);
				let entry = parentGroupsByDefinition.get(definitionKey);
				if (!entry) {
					const groupNumber = nextGroupNumber++;
					const groupKey = `group_${groupNumber}_main`;
					entry = { groupNumber, groupKey };
					parentGroupsByDefinition.set(definitionKey, entry);
					groupInfoByKey.set(groupKey, {
						label: `Group ${groupNumber + 1} - Main`,
						groupIndex: groupNumber,
						baseGroupIndex: groupNumber,
						distanceValue: null,
						isCross: false
					});
				}
				return entry;
			};

			const normalizeDistanceMeta = (value: any): { numeric: number | null; display: string; keyPart: string } => {
				const numeric = Number(value);
				if (!Number.isFinite(numeric)) {
					return { numeric: null, display: "0", keyPart: "0" };
				}
				const isInt = Math.abs(numeric % 1) < 1e-6;
				const displayRaw = isInt ? numeric.toFixed(0) : numeric.toFixed(2);
				const display = displayRaw.replace(/\.0+$/, "").replace(/\.([1-9]*)0+$/, ".$1");
				const keyPartBase = isInt ? numeric.toFixed(0) : numeric.toFixed(3);
				const keyPart = keyPartBase.replace(/-/g, "neg").replace(/\./g, "_");
				return {
					numeric,
					display: display.length ? display : numeric.toString(),
					keyPart: keyPart.length ? keyPart : "0"
				};
			};

			const assignGroupInfoForRecord = (record: any) => {
				const idStr = String(record.id);
				const hasParent = !(record?.parent_profile === undefined || record?.parent_profile === null || String(record.parent_profile).trim().length === 0);
				if (hasParent) {
					const parentId = String(record.parent_profile);
					const parentInfo = groupMapping.get(parentId);
					if (parentInfo) {
						const parentGroupNumber = parentInfo.baseGroupIndex ?? parentInfo.groupIndex ?? 0;
						const distanceMeta = normalizeDistanceMeta(record?.distance);
						const groupKey = `group_${parentGroupNumber}_distance_${distanceMeta.keyPart}`;
						if (!groupInfoByKey.has(groupKey)) {
							groupInfoByKey.set(groupKey, {
								label: `Group ${parentGroupNumber + 1} - Distance ${distanceMeta.display}`,
								groupIndex: parentGroupNumber,
								baseGroupIndex: parentGroupNumber,
								distanceValue: distanceMeta.numeric,
								isCross: true
							});
						} else {
							const existing = groupInfoByKey.get(groupKey)!;
							if (existing.distanceValue == null && distanceMeta.numeric != null) {
								existing.distanceValue = distanceMeta.numeric;
							}
						}
						const info = groupInfoByKey.get(groupKey)!;
						const result = {
							key: groupKey,
							label: info.label,
							groupIndex: info.groupIndex,
							baseGroupIndex: info.baseGroupIndex,
							distanceValue: info.distanceValue,
							isCross: true
						};
						groupMapping.set(idStr, result);
						return result;
					}
				}

				const parentGroup = getOrCreateParentGroup(record);
				const info = groupInfoByKey.get(parentGroup.groupKey)!;
				const fallback = {
					key: parentGroup.groupKey,
					label: info.label,
					groupIndex: info.groupIndex,
					baseGroupIndex: info.baseGroupIndex,
					distanceValue: info.distanceValue,
					isCross: hasParent
				};
				groupMapping.set(idStr, fallback);
				return fallback;
			};

			records.forEach((record: any) => {
				if (!isParentRecord(record)) {
					return;
				}
				const idStr = String(record.id);
				const parentGroup = getOrCreateParentGroup(record);
				const info = groupInfoByKey.get(parentGroup.groupKey)!;
				groupMapping.set(idStr, {
					key: parentGroup.groupKey,
					label: info.label,
					groupIndex: info.groupIndex,
					baseGroupIndex: info.baseGroupIndex,
					distanceValue: info.distanceValue,
					isCross: info.isCross
				});
			});

			for (const r of records) {
				const pts3 = Array.isArray(r.line_3d) ? toVecs3(r.line_3d) : [];
				if (!pts3.length) continue;
				const localOrigin = new THREE.Vector3();
				for (const p of pts3) localOrigin.add(p);
				localOrigin.multiplyScalar(1 / pts3.length);
				const rel = pts3.map(p => p.clone().sub(localOrigin));
				const geom = new THREE.BufferGeometry().setFromPoints(rel);
				geom.computeBoundingBox();
				geom.computeBoundingSphere();
				const material = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 5 });
				const line = new Profile(geom, material);
				line.unit = this.viewer.unit;
				line.position.copy(localOrigin);
				const idStr = String(r.id);
				const uniqueId = `profile_${idStr}_${profileCounter++}`;
				let groupInfo = groupMapping.get(idStr);
				if (!groupInfo) {
					groupInfo = assignGroupInfoForRecord(r);
				}
				const groupKey = groupInfo?.key ?? `group_${idStr}`;
				const fallbackIdx = parseInt(groupKey.replace(/[^0-9]+/g, ""), 10);
				const fallbackGroupNumber = Number.isFinite(fallbackIdx) ? fallbackIdx + 1 : undefined;
				const groupLabel = groupInfo?.label ?? (fallbackGroupNumber ? `Group ${fallbackGroupNumber} - Main` : `Group ${groupKey} - Main`);
				const chartPoints = toChartPoints(r.line_2d, pts3);
				if (chartPoints.length) {
					line.setChartPoints(chartPoints);
				}
				line.uuid = uniqueId;
				line.name = r.name || `Profile ${idStr}`;
				const parentProfile = r.parent_profile != null ? String(r.parent_profile) : undefined;
				const distanceRaw = r.distance != null ? Number(r.distance) : undefined;
				const groupDistance = groupInfo?.distanceValue ?? null;
				const distanceNumeric = Number.isFinite(distanceRaw ?? NaN)
					? distanceRaw
					: (Number.isFinite(groupDistance ?? NaN) ? groupDistance! : undefined);
				const groupIndex = groupInfo?.groupIndex;
				const baseGroupIndex = groupInfo?.baseGroupIndex ?? groupIndex ?? null;
				const isCross = groupInfo?.isCross === true || parentProfile != null;
				line.userData = {
					layerName: groupLabel,
					elementType: "profile",
					profileId: idStr,
					groupId: groupKey,
					groupKey,
					groupLabel,
					parentId: parentProfile,
					distance: Number.isFinite(distanceNumeric!) ? distanceNumeric : undefined,
					distanceValue: Number.isFinite(distanceNumeric!) ? distanceNumeric : undefined,
					groupIndex,
					baseGroupIndex,
					groupDisplayNumber: typeof baseGroupIndex === "number" ? baseGroupIndex + 1 : undefined,
					isCrossSection: isCross
				};
				this.viewer.profilesGroup.add(line);
				this.profiles.push(line);
				ensureGroup(groupKey, groupLabel).profiles.push(line);
			}
		}

		this.createLayerHierarchy(profileGroups);
		if (this.profileLayers.length > 0) this.activeProfileLayer = this.profileLayers[0];
		this.viewer.updateLayers();
		document.dispatchEvent(new CustomEvent("profile-data-loaded"));
		console.log("Loaded profiles from JSON", url);
		return true;
	}

	private createLayerHierarchy(profileGroups: Map<string, ProfileGroup>): void {
		const allProfiles = Array.from(profileGroups.values()).reduce((arr, g) => arr.concat(g.profiles), [] as Profile[]);
		const uniqueParentIds = Array.from(
			new Set(
				allProfiles.map(p => {
					const parentId = p.userData?.parentId;
					return parentId !== undefined && parentId !== null ? String(parentId) : String(p.userData?.profileId);
				})
			)
		);
		const idToColor = new Map<string, string>();
		uniqueParentIds.forEach((id) => {
			idToColor.set(id, getViewerColorFromId(id));
		});

		profileGroups.forEach(({ label, profiles }, groupId) => {
			if (!profiles.length) {
				return;
			}

			const parentLayer: LayerItem = {
				id: `group_${groupId}`,
				name: label,
				visible: true,
				layerType: LayerItemType.Profiles,
				layerStatus: LayerStatus.ProcessedAndLoaded,
				children: []
			};
			profiles.sort((a, b) => a.name.localeCompare(b.name));
			profiles.forEach((profile) => {
				const rawParentId = profile.userData?.parentId;
				const colorKey = rawParentId !== undefined && rawParentId !== null
					? String(rawParentId)
					: String(profile.userData?.profileId);
				const color = idToColor.get(colorKey) ?? generateViewerColorPalette(1)[0];
				profile.setColorFromHex(color);

				const childLayer: LayerItem = {
					id: profile.uuid,
					name: profile.name,
					visible: true,
					layerType: LayerItemType.Profiles,
					layerStatus: LayerStatus.ProcessedAndLoaded,
					parent: parentLayer.id
				};
				if (!parentLayer.children) {
					parentLayer.children = [];
				}
				parentLayer.children.push(childLayer);
				profile.updateVisibility(true);
			});
			this.profileLayers.push(parentLayer);
		});
	}

	private scaleHoverMarker(marker: THREE.Object3D, pos: THREE.Vector3): void {
		const camera = this.viewer.scene.getActiveCamera();
		const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		const clientWidth = renderAreaSize.width;
		const clientHeight = renderAreaSize.height;
		const distance = camera.position.distanceTo(pos);
		const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
		const scale = (15 / pr);
		marker.scale.set(scale, scale, scale);
	}

	private isDrawOrderOnTop(a: THREE.Intersection, b: THREE.Intersection): boolean {
		const objA: any = a.object;
		const objB: any = b.object;
		const roA = objA.renderOrder || 0;
		const roB = objB.renderOrder || 0;
		if (roA !== roB) return roA > roB;

		const profA: any = objA.originalProfile || objA;
		const profB: any = objB.originalProfile || objB;
		const idxA = this.viewer.profilesGroup.children.indexOf(profA);
		const idxB = this.viewer.profilesGroup.children.indexOf(profB);
		return idxA > idxB;
	}

	private calculateDistanceAlongProfile(profile: any, point: THREE.Vector3): number | null {
		if (!profile.chartPoints || profile.chartPoints.length === 0) return null;

		const worldPoints = profile.getWorldPoints ? profile.getWorldPoints() : [];
		if (worldPoints.length < 2 || worldPoints.length !== profile.chartPoints.length) return null;

		let closestDistanceToLine = Infinity;
		let closestSegmentIndex = -1;
		let closestT = 0;

		for (let i = 0; i < worldPoints.length - 1; i++) {
			const segmentStart = worldPoints[i];
			const segmentEnd = worldPoints[i + 1];
			const line = new THREE.Line3(segmentStart, segmentEnd);
			const projectedPoint = new THREE.Vector3();
			line.closestPointToPoint(point, true, projectedPoint);

			const distance = point.distanceTo(projectedPoint);
			if (distance < closestDistanceToLine) {
				closestDistanceToLine = distance;
				closestSegmentIndex = i;

				const segmentLength = segmentStart.distanceTo(segmentEnd);
				if (segmentLength > 0) {
					closestT = segmentStart.distanceTo(projectedPoint) / segmentLength;
					closestT = Math.max(0, Math.min(1, closestT));
				} else {
					closestT = 0;
				}
			}
		}

		if (closestSegmentIndex === -1) return null;

		const chartPoint1 = profile.chartPoints[closestSegmentIndex];
		const chartPoint2 = profile.chartPoints[closestSegmentIndex + 1];
		if (!chartPoint1 || !chartPoint2) return null;

		return chartPoint1.distance + (chartPoint2.distance - chartPoint1.distance) * closestT;
	}

	private handleProfileHover(intersection: THREE.Intersection): void {
		const profile = intersection.object;
		if (!profile || !profile.uuid) return;

		const hoverPoint = intersection.point;
		this.current3DHoveredProfileId = profile.uuid;
		this.setHoverMarker(profile.uuid, hoverPoint);

		const distance = this.calculateDistanceAlongProfile(profile, hoverPoint);
		if (distance !== null) {
			this.viewer.onProfileHover.emit({ profileId: profile.uuid, distance });
		}
	}

	private clearAll3DHoverMarkers(): void {
		if (this.current3DHoveredProfileId) {
			this.setHoverMarker(this.current3DHoveredProfileId, null);
			this.current3DHoveredProfileId = null;
			this.viewer.onProfileHover.emit(null);
		}
	}

	public convert(toMapCoordinates: boolean): void {
		if (!this.viewer.projectCoordinateSystem) {
			return;
		}

		// Convert profiles: rebuild geometry from converted world points
		for (const prof of this.profiles) {
			const profile: any = prof as any;
			const isInMap = profile.hasMapCoordinates === true;
			if (isInMap === toMapCoordinates) continue;

			const convertPos = (p: THREE.Vector3) => {
				if (!p) return;
				if (toMapCoordinates) {
					const np = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).forward([p.x, p.y, p.z]);
					p.set(np[0], np[1], np[2]);
				} else {
					const np = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).inverse([p.x, p.y, p.z]);
					p.set(np[0], np[1], np[2]);
				}
			};

			// Build world points from current geometry (relative points + origin)
			const relPoints: THREE.Vector3[] = (profile.points || []) as THREE.Vector3[];
			const worldPoints: THREE.Vector3[] = relPoints.map((p: THREE.Vector3) => p.clone().add((profile as THREE.Line).position));
			for (const wp of worldPoints) convertPos(wp);
			// Compute new local origin as centroid
			const newOrigin = worldPoints.reduce((acc: THREE.Vector3, v: THREE.Vector3) => acc.add(v), new THREE.Vector3()).multiplyScalar(worldPoints.length > 0 ? (1 / worldPoints.length) : 1);
			const newRelPoints = worldPoints.map((w: THREE.Vector3) => w.clone().sub(newOrigin));

			// Update original line geometry (avoid leading cast expression for transpilers)
			const _geom = (profile as any).geometry as THREE.BufferGeometry;
			_geom.setFromPoints(newRelPoints);
			// After changing geometry positions, ensure bounds and attribute state are updated
			const posAttr = _geom.getAttribute("position");
			if (posAttr) (posAttr as any).needsUpdate = true;
			_geom.computeBoundingBox();
			_geom.computeBoundingSphere();
			(profile as any).points = newRelPoints.map((p: THREE.Vector3) => p.clone());
			(profile as THREE.Line).position.copy(newOrigin);

			// Update Line2 (thick line) geometry if present
			const maybeLine2 = (profile as any).line2 as Line2 | null;
			if (maybeLine2 && maybeLine2.geometry) {
				const flat: number[] = [];
				for (const p of newRelPoints) flat.push(p.x, p.y, p.z);
				const _line2Geom = (maybeLine2 as any).geometry as LineGeometry;
				_line2Geom.setPositions(flat);
				maybeLine2.position.copy(newOrigin);
				maybeLine2.computeLineDistances();
			}

			profile.hasMapCoordinates = toMapCoordinates;
		}
	}

	public cleanup(): void {
		this.clearHoverMarkers();
		while (this.profiles.length > 0) {
			const profile = this.profiles[0];
			this.profiles.splice(0, 1);
			if (profile.parent) {
				profile.parent.remove(profile);
			}
		}
		this.profileLayers = [];
		this.activeProfileLayer = null;
		this.selectedProfiles = [];
	}

	/**
	 * Handle profile selection (both single and group selection)
	 * Returns an object representing the selection that should be set as selectedElement
	 */
	public handleProfileClick(clickedProfile: Profile, isMultiSelect: boolean): { selectedElement: any; didChange: boolean } {
		if (isMultiSelect) {
			// Ctrl/Cmd+click: toggle profile in multi-selection
			this.handleMultiSelection(clickedProfile);
			return { selectedElement: null, didChange: true };
		}

		// Regular click: select entire profile group
		this.clearMultiSelection();

		// Find all profiles in the same group
		const clickedGroupKey = (clickedProfile as any).userData?.groupKey;
		const groupProfiles: Profile[] = [];

		if (clickedGroupKey) {
			for (const profile of this.profiles) {
				if ((profile as any).userData?.groupKey === clickedGroupKey) {
					groupProfiles.push(profile);
					this.viewer.selectObject(profile, true);
					this.selectedProfiles.push(profile);
				}
			}
		}

		// Create a profile group selection object
		if (groupProfiles.length > 0) {
			const groupLabel = (clickedProfile as any).userData?.groupLabel ||
								(clickedProfile as any).userData?.layerName ||
								clickedProfile.name;

			// Find the layer item for this group
			let layerItem = null;
			for (const layer of this.profileLayers) {
				if (layer.name === groupLabel) {
					layerItem = layer;
					// Expand the group if not already expanded
					if (!layer.expanded) {
						layer.expanded = true;
					}
					break;
				}
			}

			const selectedElement = {
				isProfileGroup: true,
				groupName: groupLabel,
				layerItem: layerItem,
				elementType: "profileGroup",
				profiles: groupProfiles
			} as any;

			// Notify UI to scroll to the selected group
			if (layerItem) {
				document.dispatchEvent(new CustomEvent("profile-group-selected", {
					detail: { groupName: groupLabel, layerItem: layerItem }
				}));
			}

			return { selectedElement, didChange: true };
		}

		return { selectedElement: null, didChange: true };
	}
}
